import { useCallback } from 'react';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import { dayjs } from 'util/shared/date';
import {
  DEFAULT_TERM_STRUCTURE_CONFIG,
  ExpirationsDisplayType,
  GreeksStatsLookback,
  TermStructureConfig,
} from 'types/impliedVol';

/** URL-serializable shape (no Dayjs — tradeDate is reconstructed from key) */
interface SerializedDateEntry {
  key: string;
  color: string;
  currentPrice: number;
}

function parseSerializedDates(value: unknown): SerializedDateEntry[] {
  if (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === 'object' &&
        v != null &&
        'key' in v &&
        typeof (v as SerializedDateEntry).key === 'string',
    )
  ) {
    return value as SerializedDateEntry[];
  }
  return [];
}

const TS_QUERY_KEYS = {
  ts_disp: parseAsStringEnum<ExpirationsDisplayType>(
    Object.values(ExpirationsDisplayType),
  ).withDefault(DEFAULT_TERM_STRUCTURE_CONFIG.displayType),
  ts_econ: parseAsBoolean.withDefault(
    DEFAULT_TERM_STRUCTURE_CONFIG.showEconomicEvents,
  ),
  ts_fiv: parseAsBoolean.withDefault(
    DEFAULT_TERM_STRUCTURE_CONFIG.showForwardIVs,
  ),
  ts_stats: parseAsBoolean.withDefault(DEFAULT_TERM_STRUCTURE_CONFIG.showStats),
  ts_sgiv: parseAsBoolean.withDefault(
    DEFAULT_TERM_STRUCTURE_CONFIG.useSpotGammaIV,
  ),
  ts_lb: parseAsStringEnum<GreeksStatsLookback>(
    Object.values(GreeksStatsLookback),
  ).withDefault(DEFAULT_TERM_STRUCTURE_CONFIG.statsLookback),
  ts_dates: parseAsJson<SerializedDateEntry[]>(
    parseSerializedDates,
  ).withDefault([]),
  ts_hidden: parseAsArrayOf(parseAsString).withDefault(
    DEFAULT_TERM_STRUCTURE_CONFIG.hiddenDates,
  ),
  ts_div: parseAsBoolean.withDefault(
    DEFAULT_TERM_STRUCTURE_CONFIG.showDividends,
  ),
};

export function useTermStructureConfig(): [
  TermStructureConfig,
  (patch: Partial<TermStructureConfig>) => void,
] {
  const [params, setParams] = useQueryStates(TS_QUERY_KEYS);

  const config: TermStructureConfig = {
    displayType: params.ts_disp,
    showEconomicEvents: params.ts_econ,
    showForwardIVs: params.ts_fiv,
    showStats: params.ts_stats,
    showDividends: params.ts_div,
    useSpotGammaIV: params.ts_sgiv,
    statsLookback: params.ts_lb,
    additionalDates: params.ts_dates.map((e) => ({
      key: e.key,
      color: e.color,
      currentPrice: e.currentPrice,
      tradeDate: dayjs(e.key),
    })),
    hiddenDates: params.ts_hidden,
  };

  const updateConfig = useCallback(
    (patch: Partial<TermStructureConfig>) => {
      const urlPatch: Partial<typeof params> = {};

      if (patch.displayType != null) {
        urlPatch.ts_disp = patch.displayType;
      }
      if (patch.showEconomicEvents != null) {
        urlPatch.ts_econ = patch.showEconomicEvents;
      }
      if (patch.showForwardIVs != null) {
        urlPatch.ts_fiv = patch.showForwardIVs;
      }
      if (patch.showStats != null) {
        urlPatch.ts_stats = patch.showStats;
      }
      if (patch.useSpotGammaIV != null) {
        urlPatch.ts_sgiv = patch.useSpotGammaIV;
      }
      if (patch.showDividends != null) {
        urlPatch.ts_div = patch.showDividends;
      }
      if (patch.statsLookback != null) {
        urlPatch.ts_lb = patch.statsLookback;
      }
      if (patch.additionalDates != null) {
        urlPatch.ts_dates = patch.additionalDates.map(
          ({ key, color, currentPrice }) => ({ key, color, currentPrice }),
        );
      }
      if (patch.hiddenDates != null) {
        urlPatch.ts_hidden = patch.hiddenDates;
      }

      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
